// @ts-nocheck

import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from '@/hooks/use-toast';
import { MessageSquare, Users, UserPlus, Check, Search, UserCircle2, Plus, ShieldAlert } from 'lucide-react';
import ChatWindow from './ChatWindow';
import ChatLauncher from '@/components/chat/ChatLauncher';
import { listAllUsers, searchUsers, getRelationships, sendFriendRequest, acceptFriendRequest, listFriends, listFriendRequests, declineFriendRequest, cancelFriendRequest } from '@/services/friends';
import { ensureDmRoom, createGroupRoom } from '@/services/chat';
import { usePresence } from '@/hooks/usePresence';
import { useLastSeen } from '@/hooks/useLastSeen';
import { playSound } from '@/utils/sound';

const MessagesTab: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'all' | 'friends' | 'requests' | 'recent'>('all');
  const [q, setQ] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [relationships, setRelationships] = useState<any[]>([]);
  const [friends, setFriends] = useState<string[]>([]);
  const [requests, setRequests] = useState<{ incoming: any[]; outgoing: any[] }>({ incoming: [], outgoing: [] });
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [selectedForGroup, setSelectedForGroup] = useState<Set<string>>(new Set());
  const [activeRoomId, setActiveRoomId] = useState<string | null>(null);
  const [activeRoomTitle, setActiveRoomTitle] = useState<string>('');
  const { onlineUsers } = usePresence();
  useLastSeen();

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      try {
        setLoading(true);
        const [all, rels, fr, req] = await Promise.all([
          listAllUsers(50, 0),
          getRelationships(),
          listFriends(),
          listFriendRequests(),
        ]);
        setUsers(all);
        setRelationships(rels);
        setFriends(fr);
        setRequests(req);
      } catch (e: any) {
        console.error(e);
        toast({ title: 'Error', description: e.message || 'Failed to load users', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id]);

  const relationshipMap = useMemo(() => {
    const map = new Map<string, 'pending' | 'accepted' | 'blocked' | 'none' | 'outgoing'>();
    relationships.forEach((r) => {
      const other = r.user_id === user?.id ? r.other_user_id : r.user_id;
      if (r.status === 'pending') {
        map.set(other, r.user_id === user?.id ? 'outgoing' : 'pending');
      } else {
        map.set(other, r.status);
      }
    });
    return map;
  }, [relationships, user?.id]);

  const filteredUsers = useMemo(() => {
    let list = users;
    if (activeTab === 'friends') {
      list = users.filter(u => friends.includes(u.id));
    } else if (activeTab === 'requests') {
      const incomingSet = new Set(requests.incoming.map(r => r.user_id));
      list = users.filter(u => incomingSet.has(u.id));
    }
    if (q) {
      const lower = q.toLowerCase();
      list = list.filter(u => `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase().includes(lower));
    }
    return list;
  }, [users, friends, requests, activeTab, q]);

  const handleSearch = async (value: string) => {
    setQ(value);
    if (!value) return;
    try {
      const res = await searchUsers(value, 20);
      setUsers(res);
    } catch (e: any) {
      console.error(e);
    }
  };

  const handleAddFriend = async (id: string) => {
    try {
      await sendFriendRequest(id);
      toast({ title: 'Friend request sent' });
      playSound('request');
      const rels = await getRelationships();
      setRelationships(rels);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to send request', variant: 'destructive' });
    }
  };

  const handleAccept = async (id: string) => {
    try {
      await acceptFriendRequest(id);
      toast({ title: 'Friend request accepted' });
      playSound('accepted');
      const [rels, fr, req] = await Promise.all([getRelationships(), listFriends(), listFriendRequests()]);
      setRelationships(rels);
      setFriends(fr);
      setRequests(req);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to accept request', variant: 'destructive' });
    }
  };

  const handleDecline = async (id: string) => {
    try {
      await declineFriendRequest(id);
      toast({ title: 'Request declined' });
      const [rels, req] = await Promise.all([getRelationships(), listFriendRequests()]);
      setRelationships(rels);
      setRequests(req);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to decline', variant: 'destructive' });
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await cancelFriendRequest(id);
      toast({ title: 'Request cancelled' });
      const [rels, req] = await Promise.all([getRelationships(), listFriendRequests()]);
      setRelationships(rels);
      setRequests(req);
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to cancel', variant: 'destructive' });
    }
  };

  const handleMessage = async (id: string) => {
    try {
      if (!friends.includes(id)) {
        toast({ title: 'Friends only', description: 'You can only message friends.' });
        return;
      }
      const roomId = await ensureDmRoom(id);
      setActiveRoomId(roomId);
      const userObj = users.find(u => u.id === id);
      setActiveRoomTitle(userObj ? `${[userObj.first_name, userObj.last_name].filter(Boolean).join(' ') || 'Chat'}` : 'Chat');
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to open chat', variant: 'destructive' });
    }
  };

  const toggleSelectForGroup = (id: string) => {
    const next = new Set(selectedForGroup);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedForGroup(next);
  };

  const handleCreateGroup = async () => {
    if (selectedForGroup.size === 0) {
      toast({ title: 'Select members', description: 'Choose at least one friend to create a group.' });
      return;
    }
    setCreatingGroup(true);
    try {
      const name = `Group ${new Date().toLocaleString()}`;
      const roomId = await createGroupRoom(name, Array.from(selectedForGroup));
      setActiveRoomId(roomId);
      setActiveRoomTitle(name);
      setSelectedForGroup(new Set());
    } catch (e: any) {
      toast({ title: 'Error', description: e.message || 'Failed to create group', variant: 'destructive' });
    } finally {
      setCreatingGroup(false);
    }
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-white">
            <MessageSquare className="h-5 w-5" />
            <h3 className="text-xl">Messages</h3>
          </div>
        </div>

        {/* Floating chat toggle above Directory */}
        <Card className="bg-white/5 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-white">Floating Chat</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-white/80">
            <div className="text-sm">Enable a bottom-right floating chat launcher on all pages.</div>
            <div className="flex items-center gap-2">
              <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400" onClick={() => { localStorage.setItem('chatLauncherEnabled', 'true'); window.dispatchEvent(new Event('chatLauncher:enabledChanged')); toast({ title: 'Enabled' }); }}>
                Enable
              </Button>
              <Button className="bg-white text-gray-900 hover:bg-white/90 border border-white" onClick={() => { localStorage.setItem('chatLauncherEnabled', 'false'); window.dispatchEvent(new Event('chatLauncher:enabledChanged')); toast({ title: 'Disabled' }); }}>
                Disable
              </Button>
            </div>
              </CardContent>
            </Card>

        <Card className="bg-white/5 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-white">
              <Users className="h-4 w-4" /> Directory
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Requests strip */}
            {requests.incoming.length > 0 && (
              <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3 text-white">
                <div className="text-sm mb-2">You have {requests.incoming.length} pending friend {requests.incoming.length === 1 ? 'request' : 'requests'}.</div>
                <div className="flex gap-2 overflow-x-auto">
                  {requests.incoming.slice(0, 10).map((r: any) => (
                    <div key={r.id} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                      <div className="text-xs text-white/80">{r.user_id.slice(0,6)}...</div>
                      <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400" onClick={() => handleAccept(r.user_id)}>Accept</Button>
                      <Button size="sm" className="bg-white text-gray-900 hover:bg-white/90 border border-white" onClick={() => handleDecline(r.user_id)}>Decline</Button>
                        </div>
                  ))}
                            </div>
                          </div>
            )}
            <div className="flex gap-2 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => handleSearch(e.target.value)} placeholder="Search users..." className="pl-8 bg-white/5 border-white/10 text-white placeholder:text-white/50" />
              </div>
              <Button disabled={creatingGroup} onClick={handleCreateGroup} className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400">
                <Plus className="h-4 w-4 mr-1" /> Create Group
              </Button>
                        </div>

            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)}>
              <TabsList className="bg-white/5 border border-white/10 rounded-xl">
                <TabsTrigger value="all" className="text-white/70 hover:text-white data-[state=active]:text-white data-[state=active]:bg-gradient-to-r from-emerald-500/30 to-teal-500/30">All Users</TabsTrigger>
                <TabsTrigger value="friends" className="text-white/70 hover:text-white data-[state=active]:text-white data-[state=active]:bg-gradient-to-r from-emerald-500/30 to-teal-500/30">Friends</TabsTrigger>
                <TabsTrigger value="requests" className="text-white/70 hover:text-white data-[state=active]:text-white data-[state=active]:bg-gradient-to-r from-emerald-500/30 to-teal-500/30">Requests {requests.incoming.length > 0 ? `(${requests.incoming.length})` : ''}</TabsTrigger>
                <TabsTrigger value="recent" className="text-white/70 hover:text-white data-[state=active]:text-white data-[state=active]:bg-gradient-to-r from-emerald-500/30 to-teal-500/30">Recent</TabsTrigger>
              </TabsList>
              <TabsContent value="all" className="mt-4" />
              <TabsContent value="friends" className="mt-4" />
              <TabsContent value="requests" className="mt-4" />
              <TabsContent value="recent" className="mt-4" />
            </Tabs>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {filteredUsers.map((u) => {
                const rel = relationshipMap.get(u.id) || 'none';
                const isFriend = friends.includes(u.id);
                const online = !!onlineUsers[u.id];
                const selected = selectedForGroup.has(u.id);
                return (
                  <div key={u.id} className={`p-4 rounded-xl border border-white/10 bg-white/5 flex items-center justify-between ${selected ? 'ring-1 ring-emerald-400/50' : ''} min-h-[88px] overflow-hidden`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`h-9 w-9 rounded-full bg-white/10 flex items-center justify-center relative overflow-hidden`}>
                        {u.avatar_url ? (
                          <img src={u.avatar_url} alt="avatar" className="h-full w-full object-cover" />
                        ) : (
                          <UserCircle2 className="h-5 w-5" />
                        )}
                        <span className={`absolute -right-1 -bottom-1 h-3 w-3 rounded-full ${online ? 'bg-green-400' : 'bg-gray-500'}`} />
                      </div>
                      <div className="max-w-[140px] sm:max-w-[180px] md:max-w-[220px] lg:max-w-[260px]">
                        <div className="text-sm text-white whitespace-normal break-words leading-snug">{[u.first_name, u.last_name].filter(Boolean).join(' ') || 'User'}</div>
                        <div className="text-xs text-white/60">{online ? 'Online' : 'Offline'}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap shrink-0">
                      {isFriend ? (
                        <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400" onClick={() => handleMessage(u.id)}>Message</Button>
                      ) : rel === 'pending' ? (
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400" onClick={() => handleAccept(u.id)}>
                            <Check className="h-4 w-4 mr-1" /> Accept
                          </Button>
                          <Button size="sm" className="bg-white text-gray-900 hover:bg-white/90 border border-white" onClick={() => handleDecline(u.id)}>Decline</Button>
                        </div>
                      ) : rel === 'outgoing' ? (
                        <div className="flex items-center gap-2">
                          <Badge className="bg-white/10 border-white/20 text-white">Requested</Badge>
                          <Button size="sm" className="bg-white text-gray-900 hover:bg-white/90 border border-white" onClick={() => handleCancel(u.id)}>Cancel</Button>
                    </div>
                      ) : (
                        <Button size="sm" onClick={() => handleAddFriend(u.id)} className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400">
                          <UserPlus className="h-4 w-4 mr-1" /> Add
                        </Button>
                      )}
                      <Button size="sm" className={`${selected ? 'bg-emerald-500/20 border-emerald-400/50 text-white' : 'bg-white text-gray-900 hover:bg-white/90 border border-white'}`} onClick={() => toggleSelectForGroup(u.id)}>
                        {selected ? 'Selected' : 'Select'}
                      </Button>
                    </div>
                  </div>
                );
              })}
                        </div>
                  </CardContent>
                </Card>

        {activeRoomId && (
          <div className="h-[60vh]">
            <ChatWindow roomId={activeRoomId} title={activeRoomTitle} onReport={(messageId) => {
              // Reporting is handled inside chat; this is a stub to show where the action will be sent
              toast({ title: 'Reported', description: 'Message flagged for review' });
            }} />
          </div>
        )}

        <Card className="bg-white/5 border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-white">
              <ShieldAlert className="h-4 w-4" /> Reporting
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-white/80">
              You can report users or messages inside chat. Admin can soft-delete and hard-delete messages.
            </div>
          </CardContent>
        </Card>
      </div>
    </ScrollArea>
  );
};

export default MessagesTab;